// src/components/atomic-crm/tasks/TaskEdit.tsx
import {
  EditBase,
  Form,
  required,
  useNotify,
  useRecordContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";                              // 👈 add
import { DeleteButton } from "@/components/ds/admin/delete-button";
import { TextInput } from "@/components/ds/admin/text-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { SelectInput } from "@/components/ds/admin/select-input";
import { SaveButton } from "@/components/ds/admin/form";
import { Skeleton } from "@/components/ds/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ds/ui/dialog";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Task } from "../types";
import { EntityTypePillSelector } from "./EntityTypePillSelector";
import { EntityAutocomplete } from "./EntityAutocomplete";
import { getEntityType, transformTaskEntityData } from "./taskEntityUtils";
import { translateChoice } from "@/i18n/utils";
import { MultiAssigneeInput } from "./MultiAssigneeInput";
import { syncActivityAssignees } from "../providers/supabase/syncActivityAssignees";
import { supabase } from "../providers/supabase/supabase";

export const TaskEdit = ({
                           open,
                           close,
                           taskId,
                         }: {
  taskId: Identifier;
  open: boolean;
  close: () => void;
}) => {
  const { taskTypes, taskPriorities, taskStatuses } = useConfigurationContext();
  const notify = useNotify();
  const translate = useTranslate();

  // Captures the assignee IDs from the form during transform,
  // so onSuccess can read them after the mutation resolves.
  const pendingAssignees = useRef<number[]>([]);            // 👈 add

  const { data: assigneesRaw, isLoading: assigneesLoading } = useQuery({
    queryKey: ["crm_activity_assignees", taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_activity_assignees")
        .select("sales_id, assigned_at")
        .eq("activity_id", taskId)
        .order("assigned_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!taskId,
  });

  const currentAssigneeIds = (assigneesRaw ?? []).map((r: any) =>
    Number(r.sales_id),
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      {taskId && assigneesLoading && (
        <DialogContent className="lg:max-w-xl">
          <div className="space-y-4 py-8">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </DialogContent>
      )}
      {taskId && !assigneesLoading && (
        <EditBase
          id={taskId}
          resource="crm_activities"
          className="mt-0"
          mutationOptions={{
            onSuccess: async () => {
              // 👇 Use the ref, not the mutation response.
              const selected = pendingAssignees.current;
              if (taskId) {
                await syncActivityAssignees(Number(taskId), selected);
              }
              pendingAssignees.current = [];
              close();
              notify(translate("crm.task.notification.updated"), {
                type: "info",
                undoable: true,
              });
            },
          }}
          redirect={false}
          transform={(data) => {
            // 👇 Capture assignee_ids before the transform strips them.
            pendingAssignees.current = Array.isArray(data.assignee_ids)
              ? data.assignee_ids.map(Number)
              : [];

            const entity = transformTaskEntityData(data);
            return {
              type: "task",
              text: data.text,
              date: data.date ?? null,
              due_date: data.due_date ?? null,
              done_date: data.done_date ?? null,
              priority: data.priority ?? null,
              status: data.status ?? null,
              contact_id: entity.contact_id ?? null,
              company_id: entity.company_id ?? null,
              deal_id: entity.deal_id ?? null,
              updated_at: new Date().toISOString(),
            };
          }}
        >
          <TaskEditForm
            taskTypes={taskTypes}
            taskPriorities={taskPriorities}
            taskStatuses={taskStatuses}
            onClose={close}
            currentAssigneeIds={currentAssigneeIds}
          />
        </EditBase>
      )}
    </Dialog>
  );
};


const TaskEditForm = ({
                        taskTypes,
                        taskPriorities,
                        taskStatuses,
                        onClose,
                        currentAssigneeIds,
                      }: {
  taskTypes: string[];
  taskPriorities: { id: string; name: string }[];
  taskStatuses: { id: string; name: string }[];
  onClose: () => void;
  currentAssigneeIds: number[];
}) => {
  const record = useRecordContext<Task>();
  const notify = useNotify();
  const translate = useTranslate();

  const translatedTaskTypes = taskTypes.map((type) => ({
    id: type,
    name: translateChoice(translate, "crm.task.type", type, type),
  }));

  const translatedTaskPriorities = taskPriorities.map((priority) => ({
    ...priority,
    name: translateChoice(
      translate,
      "crm.task.priority",
      priority.id,
      priority.name,
    ),
  }));

  const translatedTaskStatuses = taskStatuses.map((status) => ({
    ...status,
    name: translateChoice(translate, "crm.task.status", status.id, status.name),
  }));

  return (
    <DialogContent className="lg:max-w-xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
      <Form
        className="flex flex-col gap-4"
        defaultValues={{
          entity_type: record
            ? (getEntityType(record) ?? "contact")
            : "contact",
          contact_id: record?.contact_id ?? null,
          company_id: record?.company_id ?? null,
          deal_id: record?.deal_id ?? null,
          assignee_ids: currentAssigneeIds,
        }}
      >
        <DialogHeader>
          <DialogTitle>{translate("crm.task.dialog.edit.title")}</DialogTitle>
          <DialogDescription>
            {translate("crm.task.dialog.edit.description")}
          </DialogDescription>
        </DialogHeader>
        <TextInput
          autoFocus
          source="text"
          label={translate("crm.task.field.description")}
          validate={required()}
          multiline
          helperText={false}
        />

        <div className="grid grid-cols-2 gap-4 mt-4">
          <EntityTypePillSelector />
        </div>

        <div className="mt-4">
          <EntityAutocomplete helperText={false} />
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <DateTimeInput
            source="due_date"
            helperText={false}
            validate={required()}
          />
          <SelectInput
            source="type"
            choices={translatedTaskTypes}
            helperText={false}
            validate={required()}
            label={translate("crm.task.field.type")}
          />
          <SelectInput
            source="priority"
            choices={translatedTaskPriorities}
            helperText={false}
          />
          <SelectInput
            source="status"
            choices={translatedTaskStatuses}
            helperText={false}
          />
        </div>

        <div className="mt-4">
          <MultiAssigneeInput
            source="assignee_ids"
            helperText="People who need to act on this."
          />
        </div>

        <DialogFooter className="w-full sm:justify-between gap-4">
          <DeleteButton
            mutationOptions={{
              onSuccess: () => {
                onClose();
                notify(translate("crm.task.notification.deleted"), {
                  type: "info",
                  undoable: true,
                });
              },
            }}
            redirect={false}
          />
          <SaveButton />
        </DialogFooter>
      </Form>
    </DialogContent>
  );
};